/**
 * MaintenanceScreen — "Coming Soon" holding page.
 *
 * ADMIN BYPASS:
 *   Tap the "M-Vitations" text at the bottom 5 times within 3 seconds.
 *   A password modal appears. Enter the admin password.
 *   On success, a backend-signed token is stored in localStorage.
 *   The maintenance gate reads that token and grants full site access.
 *   Token persists across refreshes until localStorage is cleared.
 *
 * NO URL PARAM NEEDED — fully self-contained on the page.
 */

import React, { useState, useRef, useCallback } from 'react';
import TOKENS from '../data/tokens';

const API = process.env.REACT_APP_BACKEND_URL;

const GOLD        = TOKENS.colors.textGold;
const TAP_COUNT   = 5;      // taps required
const TAP_WINDOW  = 3000;   // ms within which taps must occur

// ─── Password Modal ───────────────────────────────────────────────────────────
function AdminModal({ onClose }) {
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [success,  setSuccess]  = useState(false);

  const submit = async () => {
    if (!password.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/api/auth/bypass-access`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem('admin_bypass_token', data.token);
        setSuccess(true);
        setTimeout(() => window.location.reload(), 800);
      } else {
        setError('Incorrect password.');
        setPassword('');
      }
    } catch {
      setError('Unable to connect. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') submit();
    if (e.key === 'Escape') onClose();
  };

  return (
    /* Backdrop */
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(20,16,10,0.55)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      {/* Modal card */}
      <div style={{
        background: 'rgba(255,254,250,0.97)',
        borderRadius: 18,
        padding: '36px 32px 28px',
        maxWidth: 360, width: '100%',
        textAlign: 'center',
        boxShadow: '0 24px 64px rgba(0,0,0,0.18), 0 1px 0 rgba(255,255,255,0.90)',
        border: `1px solid rgba(212,168,83,0.25)`,
        position: 'relative',
      }}>
        {/* Gold top accent */}
        <div style={{
          position: 'absolute', top: 0, left: '50%',
          transform: 'translateX(-50%)',
          width: 60, height: 2.5,
          background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
          borderRadius: '0 0 3px 3px',
        }} />

        {success ? (
          <>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: 13,
              color: '#2D7A4F', letterSpacing: '1px', textTransform: 'uppercase', margin: 0 }}>
              Access Granted
            </p>
          </>
        ) : (
          <>
            <p style={{
              fontFamily: "'Cinzel', serif",
              fontSize: 11, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '2px',
              color: 'rgba(140,100,20,0.80)', margin: '0 0 8px',
            }}>
              Admin Access
            </p>
            <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 22, fontWeight: 400, fontStyle: 'italic',
              color: '#1E1A14', margin: '0 0 24px',
            }}>
              Enter your password
            </p>

            <input
              autoFocus
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              onKeyDown={handleKey}
              placeholder="Password"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '11px 14px', borderRadius: 9, marginBottom: 10,
                border: `1.5px solid ${error ? 'rgba(180,60,60,0.55)' : 'rgba(200,170,60,0.40)'}`,
                background: '#FFFEF8', fontSize: 15,
                color: '#1E1A14', outline: 'none', fontFamily: 'inherit',
                textAlign: 'center', letterSpacing: '2px',
              }}
            />

            {error && (
              <p style={{
                fontSize: 12, color: '#9B2020', margin: '0 0 10px',
                fontStyle: 'italic',
              }}>
                {error}
              </p>
            )}

            <button
              onClick={submit}
              disabled={loading || !password.trim()}
              style={{
                width: '100%', padding: '12px',
                borderRadius: 9, border: 'none', cursor: 'pointer',
                background: `linear-gradient(135deg, ${GOLD}, #B89040)`,
                color: '#1B1812',
                fontFamily: "'Cinzel', serif",
                fontSize: 10, fontWeight: 700,
                letterSpacing: '2px', textTransform: 'uppercase',
                opacity: (loading || !password.trim()) ? 0.6 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              {loading ? 'Verifying…' : 'Enter'}
            </button>

            <button
              onClick={onClose}
              style={{
                marginTop: 12, background: 'none', border: 'none',
                cursor: 'pointer', fontSize: 11, color: 'rgba(120,100,60,0.55)',
                fontFamily: 'inherit', letterSpacing: '0.5px',
              }}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const MaintenanceScreen = () => {
  const [showModal, setShowModal] = useState(false);

  // Tap-counter state — 5 taps within TAP_WINDOW ms
  const tapCount  = useRef(0);
  const tapTimer  = useRef(null);

  const handleLogoTap = useCallback(() => {
    tapCount.current += 1;

    // Reset timer on each tap
    clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => { tapCount.current = 0; }, TAP_WINDOW);

    if (tapCount.current >= TAP_COUNT) {
      tapCount.current = 0;
      clearTimeout(tapTimer.current);
      setShowModal(true);
    }
  }, []);

  return (
    <>
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${TOKENS.colors.backgroundCream || '#FDF9F3'} 0%, #FAF8F5 50%, ${TOKENS.colors.backgroundCream || '#FDF9F3'} 100%)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
        position: 'relative',
        overflow: 'hidden',
      }}>

        {/* Decorative blobs */}
        <div style={{ position: 'absolute', top: '10%', left: '5%', width: 300, height: 300,
          borderRadius: '50%', pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(212,168,83,0.08) 0%, transparent 70%)' }} />
        <div style={{ position: 'absolute', bottom: '15%', right: '10%', width: 400, height: 400,
          borderRadius: '50%', pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(232,164,184,0.06) 0%, transparent 70%)' }} />

        {/* Main content card — unchanged */}
        <div style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          borderRadius: 24, padding: '60px 48px', maxWidth: 520, width: '100%',
          textAlign: 'center',
          boxShadow: '0 8px 40px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
          border: `1px solid rgba(212,168,83,0.15)`,
          position: 'relative', zIndex: 1,
        }}>

          {/* Gold top accent */}
          <div style={{
            position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
            width: 80, height: 3,
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
            borderRadius: '0 0 4px 4px',
          }} />

          {/* Icon */}
          <div style={{
            width: 64, height: 64, borderRadius: '50%', margin: '0 auto 32px',
            background: 'linear-gradient(135deg, rgba(212,168,83,0.15) 0%, rgba(212,168,83,0.05) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(212,168,83,0.2)',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
              stroke={GOLD} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </div>

          <h1 style={{
            fontFamily: TOKENS.fonts.display, fontSize: 28, fontWeight: 400,
            color: TOKENS.colors.textBlack, margin: '0 0 24px',
            letterSpacing: '-0.5px', lineHeight: 1.3,
          }}>
            Coming Soon
          </h1>

          <p style={{
            fontFamily: TOKENS.fonts.body, fontSize: 16, lineHeight: 1.7,
            color: TOKENS.colors.textMuted, margin: '0 0 32px',
            maxWidth: 380, marginLeft: 'auto', marginRight: 'auto',
          }}>
            Thank you for visiting. We're currently putting the finishing touches
            on this experience and will be back shortly.
          </p>

          <p style={{
            fontFamily: TOKENS.fonts.body, fontSize: 15,
            color: GOLD, margin: 0, fontWeight: 500,
          }}>
            Please check back soon.
          </p>

          <div style={{ marginTop: 40, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 1,
              background: 'linear-gradient(90deg, transparent, rgba(212,168,83,0.3))' }} />
            <div style={{ width: 6, height: 6, borderRadius: '50%',
              background: GOLD, opacity: 0.4 }} />
            <div style={{ width: 40, height: 1,
              background: 'linear-gradient(90deg, rgba(212,168,83,0.3), transparent)' }} />
          </div>
        </div>

        {/* Footer — the hidden tap target */}
        <div style={{ marginTop: 48, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <p
            onClick={handleLogoTap}
            data-testid="maintenance-logo-tap"
            style={{
              fontFamily: TOKENS.fonts.body, fontSize: 12,
              color: 'rgba(139,125,102,0.6)',
              margin: 0, letterSpacing: '0.5px',
              cursor: 'default',           // no pointer hint to public users
              userSelect: 'none',
            }}
          >
            M-Vitations
          </p>
        </div>
      </div>

      {showModal && <AdminModal onClose={() => setShowModal(false)} />}
    </>
  );
};

export default MaintenanceScreen;
